import { supabase, OrganizerStats, generateGeohash } from '../config/supabase';
import { OrganizerProfile } from '../types/organizer.types';
import { RealtimeChannel } from '@supabase/supabase-js';
import type { Json } from '../types/supabase.generated';

/**
 * 🔧 Helper: Créer/mettre à jour la zone organisateur pour les analytics
 * Supprime les anciennes zones avant de créer la nouvelle pour éviter les doublons
 */
async function ensureOrganizerZone(userId: string, latitude: number, longitude: number, address?: string): Promise<void> {
  try {
    const gh = generateGeohash(latitude, longitude);

    console.log(`🔧 [Supabase] Mise à jour zone organisateur: ${userId.substring(0, 8)}... → ${gh}`);

    // Supprimer toutes les anciennes zones de cet organisateur
    // (un organisateur ne doit avoir qu'une seule zone active)
    const { error: deleteError } = await supabase
      .from('organizer_zones')
      .delete()
      .eq('organizer_id', userId)
      .neq('geohash', gh);

    if (deleteError) {
      console.warn('⚠️ [Supabase] Erreur nettoyage anciennes zones:', deleteError.message);
    }

    // Upsert la zone actuelle
    const { error } = await supabase
      .from('organizer_zones')
      .upsert({
        organizer_id: userId,
        geohash: gh,
        latitude,
        longitude,
        address: address || null,
      }, {
        onConflict: 'organizer_id,geohash'
      });

    if (error) {
      console.error('❌ [Supabase] ERREUR création zone organisateur:', {
        userId: userId.substring(0, 8) + '...',
        geohash: gh,
        error: error.message,
        code: error.code,
        details: error.details
      });
      throw new Error(`Failed to update organizer_zones: ${error.message}`);
    } else {
      console.log(`✅ [Supabase] Zone organisateur créée/mise à jour: ${gh}`);
    }
  } catch (error) {
    console.error('❌ [Supabase] Exception lors de la création zone organisateur:', error);
    throw error;
  }
}

/**
 * Colonnes de la vue organizers_public (lecture publique, bypass RLS)
 * Sécurité: Exclut stripe_*, onboarding_*, email_verified
 * Utilisé par getOrganizerProfile() pour les visiteurs / EventModal
 */
const ORGANIZER_VIEW_COLUMNS = `
  id,
  organizer,
  subtitle,
  description,
  organizer_image,
  gallery_images,
  email,
  phone,
  website,
  instagram,
  organizer_contact,
  location,
  location_name,
  latitude,
  longitude,
  stats,
  created_at,
  updated_at,
  geohash
` as const;

/**
 * Colonnes pour l'organisateur connecté (table organizers, RLS id=auth.uid())
 * Inclut les champs internes (onboarding, email_verified) mais exclut Stripe
 */
const ORGANIZER_PRIVATE_COLUMNS = `
  id,
  organizer,
  subtitle,
  description,
  organizer_image,
  gallery_images,
  email,
  phone,
  website,
  instagram,
  organizer_contact,
  location,
  location_name,
  latitude,
  longitude,
  stats,
  created_at,
  updated_at,
  geohash,
  onboarding_completed,
  onboarding_completed_at,
  email_verified
` as const;

/**
 * Service Supabase pour la gestion des organisateurs
 */
export const supabaseOrganizerService = {
  /**
   * Lecture publique d'un profil organisateur (visiteurs, EventModal)
   * Utilise la vue organizers_public (bypass RLS, sans données internes)
   */
  /**
   * ⚠️ PRD-robustesse-organisateur-reseau-faible.md (item 4): retourne `null`
   * uniquement quand le profil n'existe vraiment pas (PGRST116). Une erreur
   * réseau/serveur transitoire est PROPAGÉE (throw) pour que l'appelant puisse
   * la distinguer d'un "pas de profil" — les deux étaient auparavant indistinguables.
   */
  async getOrganizerProfile(organizerId: string): Promise<OrganizerProfile | null> {
    console.log('🔍 [Supabase] Chargement organisateur (public):', organizerId);

    const { data, error } = await supabase
      .from('organizers_public')
      .select(ORGANIZER_VIEW_COLUMNS)
      .eq('id', organizerId)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        console.log('❌ Organisateur non trouvé:', organizerId);
        return null;
      }
      console.error('❌ Erreur (réseau/serveur) lors du chargement de l\'organisateur:', error);
      throw error;
    }

    if (!data) {
      console.log('❌ Organisateur non trouvé:', organizerId);
      return null;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const rawData = data as any;

    const profile: OrganizerProfile = {
      id: rawData.id,
      organizer: rawData.organizer,
      subtitle: rawData.subtitle ?? undefined,
      description: rawData.description ?? undefined,
      organizerImage: rawData.organizer_image ?? undefined,
      galleryImages: rawData.gallery_images ?? [],
      email: rawData.email ?? undefined,
      phone: rawData.phone ?? undefined,
      website: rawData.website ?? undefined,
      instagram: rawData.instagram ?? undefined,
      organizerContact: rawData.organizer_contact ?? undefined,
      location: rawData.location,
      locationName: rawData.location_name,
      latitude: rawData.latitude ?? undefined,
      longitude: rawData.longitude ?? undefined,
      stats: (rawData.stats as OrganizerStats) ?? { totalEvents: 0, totalViews: 0, activeEvents: 0 },
      createdAt: rawData.created_at ? new Date(rawData.created_at) : new Date(),
      updatedAt: rawData.updated_at ? new Date(rawData.updated_at) : new Date(),
    };

    console.log('✅ Organisateur chargé (public):', organizerId);
    return profile;
  },

  /**
   * Lecture privée pour l'organisateur connecté (table organizers, RLS id=auth.uid())
   * Inclut onboarding_completed, email_verified etc.
   */
  /**
   * ⚠️ PRD-robustesse-organisateur-reseau-faible.md (item 4): retourne `null`
   * uniquement quand le profil n'existe vraiment pas (PGRST116). Une erreur
   * réseau/serveur transitoire est PROPAGÉE (throw) pour que l'appelant puisse
   * la distinguer d'un "pas de profil" (auparavant les deux cas retournaient
   * silencieusement `null`, ce qui pouvait faire croire à tort qu'aucun
   * onboarding n'était nécessaire).
   */
  async getProfile(userId: string): Promise<OrganizerProfile | null> {
    console.log('🔍 [Supabase] Chargement profil privé:', userId);

    const { data, error } = await supabase
      .from('organizers')
      .select(ORGANIZER_PRIVATE_COLUMNS)
      .eq('id', userId)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        console.log('❌ Profil non trouvé:', userId);
        return null;
      }
      console.error('❌ Erreur (réseau/serveur) lors du chargement du profil:', error);
      throw error;
    }

    if (!data) {
      console.log('❌ Profil non trouvé:', userId);
      return null;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const rawData = data as any;

    const profile: OrganizerProfile = {
      id: rawData.id,
      organizer: rawData.organizer,
      subtitle: rawData.subtitle ?? undefined,
      description: rawData.description ?? undefined,
      organizerImage: rawData.organizer_image ?? undefined,
      galleryImages: rawData.gallery_images ?? [],
      email: rawData.email ?? undefined,
      phone: rawData.phone ?? undefined,
      website: rawData.website ?? undefined,
      instagram: rawData.instagram ?? undefined,
      organizerContact: rawData.organizer_contact ?? undefined,
      location: rawData.location,
      locationName: rawData.location_name,
      latitude: rawData.latitude ?? undefined,
      longitude: rawData.longitude ?? undefined,
      stats: (rawData.stats as OrganizerStats) ?? { totalEvents: 0, totalViews: 0, activeEvents: 0 },
      createdAt: rawData.created_at ? new Date(rawData.created_at) : new Date(),
      updatedAt: rawData.updated_at ? new Date(rawData.updated_at) : new Date(),
      onboardingCompleted: rawData.onboarding_completed ?? false,
      onboardingCompletedAt: rawData.onboarding_completed_at ? new Date(rawData.onboarding_completed_at) : undefined,
      emailVerified: rawData.email_verified ?? false,
    };

    console.log('✅ Profil privé chargé:', userId);
    return profile;
  },

  /**
   * Sauvegarde un profil organisateur (création ou mise à jour complète)
   */
  async saveProfile(userId: string, profileData: Partial<OrganizerProfile>): Promise<void> {
    try {
      console.log('💾 [Supabase] Sauvegarde du profil:', userId);

      // Construire les données pour upsert (OrganizerStats -> Json)
      const upsertData = {
        id: userId,
        organizer: profileData.organizer ?? '',
        subtitle: profileData.subtitle ?? null,
        description: profileData.description ?? null,
        organizer_image: profileData.organizerImage ?? null,
        gallery_images: profileData.galleryImages ?? [],
        email: profileData.email ?? null,
        phone: profileData.phone ?? null,
        website: profileData.website ?? null,
        instagram: profileData.instagram ?? null,
        organizer_contact: profileData.organizerContact ?? null,
        location: profileData.location ?? '',
        location_name: profileData.locationName ?? '',
        latitude: profileData.latitude ?? null,
        longitude: profileData.longitude ?? null,
        stats: (profileData.stats ?? { totalEvents: 0, totalViews: 0, activeEvents: 0 }) as unknown as Json,
        updated_at: new Date().toISOString(),
      };

      const { error } = await supabase
        .from('organizers')
        .upsert(upsertData);

      if (error) {
        throw error;
      }

      // 🔧 Créer/mettre à jour la zone organisateur si coordonnées disponibles
      if (profileData.latitude && profileData.longitude) {
        await ensureOrganizerZone(
          userId,
          profileData.latitude,
          profileData.longitude,
          profileData.location
        );
      }

      console.log('✅ [Supabase] Profil sauvegardé avec succès');
    } catch (error) {
      console.error('❌ Erreur lors de la sauvegarde:', error);
      throw error;
    }
  },

  /**
   * Met à jour partiellement un profil organisateur
   */
  async updateProfile(userId: string, profileData: Partial<OrganizerProfile>): Promise<OrganizerProfile> {
    try {
      console.log('💾 [Supabase] Mise à jour du profil:', userId);

      // Construire les données de mise à jour (OrganizerStats -> Json)
      const updateData: Record<string, unknown> = {
        updated_at: new Date().toISOString(),
      };

      // Mapper les champs camelCase vers snake_case
      if (profileData.organizer !== undefined) updateData.organizer = profileData.organizer;
      if (profileData.subtitle !== undefined) updateData.subtitle = profileData.subtitle;
      if (profileData.description !== undefined) updateData.description = profileData.description;
      if (profileData.organizerImage !== undefined) updateData.organizer_image = profileData.organizerImage;
      if (profileData.galleryImages !== undefined) updateData.gallery_images = profileData.galleryImages;
      if (profileData.organizerContact !== undefined) updateData.organizer_contact = profileData.organizerContact;
      if (profileData.phone !== undefined) updateData.phone = profileData.phone;
      if (profileData.email !== undefined) updateData.email = profileData.email;
      if (profileData.website !== undefined) updateData.website = profileData.website;
      if (profileData.instagram !== undefined) updateData.instagram = profileData.instagram;
      if (profileData.location !== undefined) updateData.location = profileData.location;
      if (profileData.locationName !== undefined) updateData.location_name = profileData.locationName;
      if (profileData.latitude !== undefined) updateData.latitude = profileData.latitude;
      if (profileData.longitude !== undefined) updateData.longitude = profileData.longitude;
      if (profileData.stats !== undefined) updateData.stats = profileData.stats as unknown as Json;

      const { error } = await supabase.from('organizers').update(updateData).eq('id', userId);

      if (error) {
        throw error;
      }

      // 🔧 Créer/mettre à jour la zone organisateur si coordonnées mises à jour
      if (profileData.latitude && profileData.longitude) {
        await ensureOrganizerZone(
          userId,
          profileData.latitude,
          profileData.longitude,
          profileData.location
        );
      }

      console.log('✅ [Supabase] Profil mis à jour avec succès');

      // Récupérer le profil mis à jour (contexte authentifié → table privée)
      const updatedProfile = await supabaseOrganizerService.getProfile(userId);
      if (!updatedProfile) {
        throw new Error('Profile not found after update');
      }

      return updatedProfile;
    } catch (error) {
      console.error('❌ Erreur lors de la mise à jour:', error);
      throw error;
    }
  },

  /**
   * S'abonner aux changements du profil en temps réel
   */
  subscribeToProfile(
    userId: string,
    onUpdate: (profile: OrganizerProfile | null) => void,
    onError: (error: Error) => void
  ): () => void {
    let channel: RealtimeChannel | null = null;

    try {
      channel = supabase
        .channel(`organizer-${userId}`)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'organizers',
            filter: `id=eq.${userId}`,
          },
          async (payload) => {
            console.log('📡 [Supabase] Changement détecté:', payload.eventType);

            if (payload.eventType === 'DELETE') {
              onUpdate(null);
              return;
            }

            try {
              // Recharger le profil complet (contexte authentifié → table privée)
              const profile = await supabaseOrganizerService.getProfile(userId);
              onUpdate(profile);
            } catch (error) {
              // 🔧 FIX (item 4 - PRD robustesse réseau faible): getProfile peut
              // désormais rejeter sur une erreur réseau transitoire. On ne doit
              // PAS écraser le profil déjà affiché avec `null` dans ce cas —
              // on remonte juste l'erreur pour permettre un retry côté UI.
              onError(error instanceof Error ? error : new Error('Erreur rechargement profil'));
            }
          }
        )
        .subscribe((status) => {
          if (status === 'SUBSCRIBED') {
            console.log('✅ [Supabase] Abonnement actif pour organisateur:', userId);
          } else if (status === 'CHANNEL_ERROR') {
            onError(new Error('Erreur de connexion temps réel'));
          }
        });

      // Charger les données initiales (contexte authentifié → table privée)
      supabaseOrganizerService.getProfile(userId).then(onUpdate).catch(onError);

    } catch (error) {
      onError(error instanceof Error ? error : new Error('Erreur subscription'));
    }

    // Fonction de cleanup
    return () => {
      if (channel) {
        supabase.removeChannel(channel);
        console.log('🔌 [Supabase] Désabonnement organisateur:', userId);
      }
    };
  },

  /**
   * Marque l'onboarding comme complété pour un organisateur
   * PRD: PRD-onboarding-organisateur.md
   */
  async completeOnboarding(userId: string): Promise<void> {
    try {
      console.log('🎓 [Supabase] Marquage onboarding complété:', userId);

      const { error } = await supabase
        .from('organizers')
        .update({
          onboarding_completed: true,
          onboarding_completed_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })
        .eq('id', userId);

      if (error) {
        throw error;
      }

      console.log('✅ [Supabase] Onboarding marqué comme complété');
    } catch (error) {
      console.error('❌ Erreur lors du marquage onboarding:', error);
      throw error;
    }
  },

  /**
   * Vérifie si un organisateur a besoin d'onboarding
   * PRD: PRD-onboarding-organisateur.md
   */
  /**
   * ⚠️ PRD-robustesse-organisateur-reseau-faible.md (item 4): ne rattrape plus
   * les erreurs réseau transitoires en retournant `false` ("pas besoin
   * d'onboarding") — cela pouvait faire sauter l'onboarding à tort. getProfile()
   * rejette désormais sur une vraie erreur réseau/serveur ; on laisse cette
   * erreur remonter à l'appelant, qui doit la traiter comme "statut inconnu",
   * pas comme "onboarding non nécessaire".
   */
  async needsOnboarding(userId: string): Promise<boolean> {
    // Contexte authentifié → table privée (besoin de onboardingCompleted)
    const profile = await supabaseOrganizerService.getProfile(userId);

    if (!profile) {
      // Pas de profil = nouvel organisateur = besoin d'onboarding
      return true;
    }

    // Si onboardingCompleted est explicitement false, besoin d'onboarding
    if (profile.onboardingCompleted === false) {
      return true;
    }

    // Si onboardingCompleted est true, pas besoin
    if (profile.onboardingCompleted === true) {
      return false;
    }

    // Fallback: vérifier si le profil est incomplet (Option C hybride)
    const isProfileIncomplete =
      !profile.organizer ||
      profile.organizer === '' ||
      !profile.organizerImage ||
      !profile.location;

    return isProfileIncomplete;
  },

  /**
   * Updates the email verification status on the organizer profile
   * PRD: PRD-auth-email-verification.md
   */
  async updateEmailVerified(userId: string, verified: boolean): Promise<void> {
    try {
      console.log('📧 [Supabase] Mise à jour email_verified:', userId, verified);

      const { error } = await supabase
        .from('organizers')
        .update({
          email_verified: verified,
          updated_at: new Date().toISOString(),
        })
        .eq('id', userId);

      if (error) {
        throw error;
      }

      console.log('✅ [Supabase] email_verified mis à jour:', verified);
    } catch (error) {
      console.error('❌ Erreur mise à jour email_verified:', error);
      throw error;
    }
  },
};
